import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsletterSignup({ source = "seed-crisis", eyebrow = "Field Dispatch", headline = "Moved to act? Stay close to the work.", body = "A quiet, occasional note when there's something worth reading — new bulletins, seed system updates, and openings for practitioners.", tone = "light" }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  const isDark = tone === "dark";

  async function onSubmit(e) {
    e.preventDefault();
    if (!email || state === "loading") return;
    setState("loading");
    try {
      const res = await fetch(`${API}/subscribers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source }),
      });
      if (!res.ok) throw new Error("bad response");
      const data = await res.json();
      setState("success");
      setMessage(data.already_subscribed ? "You're already on the list." : "Thank you. We'll be in touch when it matters.");
      trackEvent("newsletter_signup", { source, already_subscribed: !!data.already_subscribed });
    } catch (err) {
      setState("error");
      setMessage("Something didn't send. Please try again in a moment.");
    }
  }

  return (
    <div
      data-testid="newsletter-signup"
      className={`border ${isDark ? "border-chalk/20 bg-bark text-chalk" : "border-rule bg-card text-ink"} p-8 md:p-10`}
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start">
        <div className="md:col-span-5">
          <p className={`specimen-label mb-3 ${isDark ? "text-chalk/60" : "text-muted"}`}>{eyebrow}</p>
          <h3 className="font-serif text-2xl md:text-3xl leading-[1.1]">{headline}</h3>
        </div>
        <div className="md:col-span-7">
          <p className={`text-sm md:text-base leading-relaxed mb-5 ${isDark ? "text-chalk/80" : "text-ink/80"}`}>
            {body}
          </p>

          {state === "success" ? (
            <div
              className={`flex items-start gap-3 py-3 ${isDark ? "text-chalk" : "text-eucalypt"}`}
              data-testid="newsletter-success"
            >
              <Check size={18} className="mt-1 shrink-0" />
              <p className="text-sm md:text-base">{message}</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3">
              <label htmlFor={`newsletter-email-${source}`} className="sr-only">Email address</label>
              <input
                id={`newsletter-email-${source}`}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@organisation.org"
                data-testid="newsletter-email-input"
                className={`flex-1 px-5 py-3 text-sm md:text-base rounded-full border focus:outline-none focus:ring-2 focus:ring-eucalypt/40 transition-all ${
                  isDark
                    ? "bg-chalk/5 border-chalk/25 text-chalk placeholder:text-chalk/40"
                    : "bg-cream border-rule text-ink placeholder:text-muted"
                }`}
              />
              <button
                type="submit"
                disabled={state === "loading"}
                data-testid="newsletter-submit"
                className="inline-flex items-center justify-center gap-2 bg-eucalypt text-chalk px-6 py-3 font-mono text-[11px] tracking-[0.22em] uppercase rounded-full hover:bg-eucalypt-dark hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {state === "loading" ? "Sending" : "Subscribe"}
                <ArrowRight size={14} />
              </button>
            </form>
          )}

          {state === "error" && (
            <p className="mt-3 text-sm text-terracotta" data-testid="newsletter-error">{message}</p>
          )}

          {state !== "success" && (
            <p className={`mt-4 text-xs italic ${isDark ? "text-chalk/50" : "text-muted"}`}>
              No spam. Unsubscribe any time. Email only — we never share your details.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
